import dayjs from 'dayjs';
import type { Map } from 'mapbox-gl';
import { is, isNil } from 'ramda';
import {
  MdOutlineChildCare,
  MdOutlineDirectionsBus,
  MdOutlineLocalCafe,
  MdOutlineRestaurant,
  MdOutlineSchool,
  MdOutlineStorefront,
  MdOutlineSubway,
  MdOutlineTrain,
  MdOutlineTram,
} from 'react-icons/md';

import { countries } from '../../../helpers/country';
import {
  NO_VALUE_PLACEHOLDER,
  isNilOrPlaceholder,
} from '../../../helpers/extra';
import BusStopMarker from '../../../images/icons/BusMarker.svg';
import CafeMarker from '../../../images/icons/CafeMarker.svg';
import DayCareMarker from '../../../images/icons/DaycareMarker.svg';
import MetroStopMarker from '../../../images/icons/MetroMarker.svg';
import RestaurantMarker from '../../../images/icons/RestaurantMarker.svg';
import SchoolMarker from '../../../images/icons/SchoolMarker.svg';
import SupermarketMarker from '../../../images/icons/SupermarketMarker.svg';
import TrainStopMarker from '../../../images/icons/TrainMarker.svg';
import TramStopMarker from '../../../images/icons/TramMarker.svg';
import BusStopsIcon from '../images/bus-stop-icon.png';
import CoffeeIcon from '../images/coffee-icon.png';
import DayCareIcon from '../images/day-care-icon.png';
import HighSchoolIcon from '../images/high-school-icon.png';
import MetroStopIcon from '../images/metro-station-icon.png';
import RestaurantIcon from '../images/restaurant-icon.png';
import SupermarketIcon from '../images/supermarket-icon.png';
import TrainStopsIcon from '../images/train-stop-icon.png';
import TramStopsIcon from '../images/tram-stop-icon.png';
import { IProperty } from '../types/types';

const isNumber = is(Number);

export const POINT_PLACEMENT_ERROR_CODE = 422;

export const COUNTRIES_WITH_STATE_SUBSIDIZED = [countries.FINLAND];

export const COUNTRIES_WITH_DEMOGRAPHICS_DATA = [
  countries.FINLAND,
  countries.DENMARK,
];

export const COUNTRIES_WITH_BUILDING_YEAR = [
  countries.FINLAND,
  countries.DENMARK,
  countries.SWEDEN,
  countries.GERMANY,
  countries.AUSTRIA,
];

export const RENT_PROPERTIES_MAP_PATH =
  '/optimize/rent-navigator/properties/map';

export const NUMBER_OF_ROOMS_DATASET: Record<string, string> = {
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5+',
};

export const LOCATION_TYPE = {
  ownLocation: 'own-locations',
  sharepoint: 'sharepoint',
} as const;

export const MARKET_COMPARISON_TYPE = {
  aboveMarket: 'aboveMarketText',
  belowMarket: 'belowMarketText',
  atMarket: 'atMarketText',
};

export type ILocationType = (typeof LOCATION_TYPE)[keyof typeof LOCATION_TYPE];

export const DEFAULT_BUILDING_YEAR_RANGE = [1900, 1940, 1980, dayjs().year()];

export const MIN_BUILD_YEAR = 1700;

export const LOCATION_ANALYSIS_TYPES = {
  radius: 'radius',
  postalCode: 'postalCode',
};

export const getCountryCurrency = (country: string | null) => {
  switch (country) {
    case countries.FINLAND:
    case countries.GERMANY:
    case countries.AUSTRIA:
      return 'EUR';
    case countries.DENMARK:
      return 'DKK';
    case countries.NORWAY:
      return 'NOK';
    case countries.SWEDEN:
      return 'SEK';
    case countries.SWITZERLAND:
      return 'CHF';
    default:
      return null;
  }
};

export const getCurrencySymbol = (country: string | null | undefined) => {
  const currencyCode = getCountryCurrency(country ?? null);

  if (!currencyCode) return '';

  const symbolPart = Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  })
    .formatToParts(0)
    .find((part) => part.type === 'currency');

  return symbolPart?.value ?? currencyCode;
};

export const formatCurrency = (
  value: string | number | null | undefined,
  country: string | null | undefined = null,
  maximumFractionDigits: number = 1,
  inThousands: boolean = false,
) => {
  const currencyCode = getCountryCurrency(country);
  if (isNilOrPlaceholder(value) || !isNumber(value)) {
    return NO_VALUE_PLACEHOLDER;
  }

  const currencyStyle: Intl.NumberFormatOptions = currencyCode
    ? {
        style: 'currency',
        currency: currencyCode,
      }
    : {};

  const formatted = Intl.NumberFormat('fi-FI', {
    ...currencyStyle,
    minimumFractionDigits: 0,
    maximumFractionDigits,
  })
    .format(inThousands ? Math.round(value / 1000) : value)
    .replace(',', '.');

  return inThousands ? formatted.replace(/(\d)(\D*)$/, '$1K$2') : formatted;
};

export const getRentSqmRange = (
  minRentPerSquareMeter: number | null | undefined,
  maxRentPerSquareMeter: number | null | undefined,
  countryCode: string | null | undefined,
) =>
  minRentPerSquareMeter && maxRentPerSquareMeter && minRentPerSquareMeter > 0
    ? `${formatCurrency(minRentPerSquareMeter)} - ${formatCurrency(maxRentPerSquareMeter, countryCode)}`
    : NO_VALUE_PLACEHOLDER;

const DWELLING_TYPES_DATASET_FI = {
  rowHouse: 'rowHouse',
  semiDetachedHouse: 'semiDetachedHouse',
  detachedHouse: 'detachedHouse',
  apartment: 'apartment',
};

const DWELLING_TYPE_DATASET_DK = {
  rentalApartment: 'rentalApartment',
  rentalHouse: 'rentalHouse',
  rentalTownHouse: 'rentalTownhouse',
};

const DWELLING_TYPES_DATASET_NO = {
  rowHouse: 'rowHouse',
  semiDetachedHouse: 'semiDetachedHouse',
  detachedHouse: 'detachedHouse',
  apartment: 'apartment',
};

const DWELLING_TYPE_DATASET_SE = {
  apartment: 'apartment',
  detachedHouse: 'detachedHouse',
  rowHouse: 'rowHouse',
  semiDetachedHouse: 'semiDetachedHouse',
};

const DWELLING_TYPES_DATASET_DE = {
  apartment: 'apartment',
  groundFloor: 'groundFloor',
  halfBasement: 'halfBasement',
  maisonette: 'maisonette',
  roofStorey: 'roofStorey',
};

const DWELLING_TYPES_DATASET_AT = {
  apartment: 'apartment',
  maisonette: 'maisonette',
  rentalHouse: 'rentalHouse',
  penthouse: 'penthouse',
  sharedApartment: 'sharedApartment',
};

const DWELLING_TYPE_DATASET_CH = {
  apartment: 'apartment',
  rentalHouse: 'rentalHouse',
};

export type CountryCode = (typeof countries)[keyof typeof countries];

export const getDwellingTypes = (country: CountryCode) => {
  if (country === countries.FINLAND) {
    return DWELLING_TYPES_DATASET_FI;
  }
  if (country === countries.DENMARK) {
    return DWELLING_TYPE_DATASET_DK;
  }
  if (country === countries.NORWAY) {
    return DWELLING_TYPES_DATASET_NO;
  }
  if (country === countries.SWEDEN) {
    return DWELLING_TYPE_DATASET_SE;
  }
  if (country === countries.GERMANY) {
    return DWELLING_TYPES_DATASET_DE;
  }
  if (country === countries.AUSTRIA) {
    return DWELLING_TYPES_DATASET_AT;
  }
  if (country === countries.SWITZERLAND) {
    return DWELLING_TYPE_DATASET_CH;
  }

  return {};
};

const AMENITY_TYPES_DATASET_FI = {
  balcony: 'balcony',
  ownSauna: 'ownSauna',
  elevator: 'elevator',
};

const AMENITY_TYPES_DATASET_DK = {
  balcony: 'balcony',
  elevator: 'elevator',
};

const AMENITY_TYPES_DATASET_NO = {
  balcony: 'balcony',
  ownSauna: 'ownSauna',
  elevator: 'elevator',
};

const AMENITY_TYPES_DATASET_SE = {
  balcony: 'balcony',
  ownSauna: 'ownSauna',
  elevator: 'elevator',
};

const AMENITY_TYPES_DATASET_DE = {
  balcony: 'balcony',
  elevator: 'elevator',
  parking: 'parking',
};

const AMENITY_TYPES_DATASET_AT = {
  balcony: 'balcony',
  elevator: 'elevator',
  parking: 'parking',
};

const AMENITY_TYPES_DATASET_CH = {
  balcony: 'balcony',
  elevator: 'elevator',
  parking: 'parking',
};

export const getAmenities = (country: CountryCode) => {
  if (country === countries.FINLAND) {
    return AMENITY_TYPES_DATASET_FI;
  }
  if (country === countries.DENMARK) {
    return AMENITY_TYPES_DATASET_DK;
  }
  if (country === countries.NORWAY) {
    return AMENITY_TYPES_DATASET_NO;
  }
  if (country === countries.SWEDEN) {
    return AMENITY_TYPES_DATASET_SE;
  }
  if (country === countries.GERMANY) {
    return AMENITY_TYPES_DATASET_DE;
  }
  if (country === countries.AUSTRIA) {
    return AMENITY_TYPES_DATASET_AT;
  }
  if (country === countries.SWITZERLAND) {
    return AMENITY_TYPES_DATASET_CH;
  }

  return {};
};

export const getMarketPositionForDiffPercentage = (
  rentDifferencePercentage: number,
): 'above' | 'below' | 'at' | undefined => {
  if (rentDifferencePercentage > 3) {
    return 'above';
  }

  if (rentDifferencePercentage < -3) {
    return 'below';
  }

  if (rentDifferencePercentage >= -3 && rentDifferencePercentage <= 3) {
    return 'at';
  }

  return undefined;
};

export const getPropertyIdFromLocationPath = (pathname: string) =>
  pathname.match(/\d+/) ? parseInt(pathname.match(/\d+/)?.[0] || '', 10) : null;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image(40, 40);
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const addReamsCustomIconToMap = async ({
  map,
  iconId,
  iconSrc,
}: {
  map: any;
  iconId: string;
  iconSrc: string;
}) => {
  if (!map || !iconSrc) return;

  try {
    if (map.hasImage(iconId)) return;

    const img = await loadImage(iconSrc);

    if (!map.hasImage(iconId)) {
      map.addImage(iconId, img, { sdf: false });
    }
  } catch {
    // Ignore failures to not break the map
  }
};

export const SERVICES_MAIN_CATEGORIES = {
  publicTransport: [
    {
      name: 'busStops',
      color: '#4e6fa8',
      icon: MdOutlineDirectionsBus,
      iconPath: BusStopsIcon,
      iconMarkerSrc: BusStopMarker,
      reactIcon: MdOutlineDirectionsBus,
    },
    {
      name: 'tramStops',
      color: '#3f7f7a',
      icon: MdOutlineTram,
      iconPath: TramStopsIcon,
      reactIcon: MdOutlineTram,
      iconMarkerSrc: TramStopMarker,
    },
    {
      name: 'trainStops',
      color: '#9e5d2d',
      icon: MdOutlineTrain,
      iconPath: TrainStopsIcon,
      reactIcon: MdOutlineTrain,
      iconMarkerSrc: TrainStopMarker,
    },
    {
      name: 'metroStops',
      color: '#c24747',
      icon: MdOutlineSubway,
      iconPath: MetroStopIcon,
      reactIcon: MdOutlineSubway,
      iconMarkerSrc: MetroStopMarker,
    },
  ],
  education: [
    {
      name: 'dayCares',
      color: '#4fd1c8',
      icon: MdOutlineChildCare,
      iconPath: DayCareIcon,
      reactIcon: MdOutlineChildCare,
      iconMarkerSrc: DayCareMarker,
    },
    {
      name: 'schools',
      color: '#95b22d',
      icon: MdOutlineSchool,
      iconPath: HighSchoolIcon,
      reactIcon: MdOutlineSchool,
      iconMarkerSrc: SchoolMarker,
    },
  ],
  retail: [
    {
      name: 'restaurants',
      color: '#8a4e8a',
      icon: MdOutlineRestaurant,
      iconPath: RestaurantIcon,
      reactIcon: MdOutlineRestaurant,
      iconMarkerSrc: RestaurantMarker,
    },
    {
      name: 'coffeePlaces',
      color: '#2d7a5f',
      icon: MdOutlineLocalCafe,
      iconPath: CoffeeIcon,
      reactIcon: MdOutlineLocalCafe,
      iconMarkerSrc: CafeMarker,
    },
    {
      name: 'supermarkets',
      color: '#e685ce',
      icon: MdOutlineStorefront,
      iconPath: SupermarketIcon,
      reactIcon: MdOutlineStorefront,
      iconMarkerSrc: SupermarketMarker,
    },
  ],
};

const SERVICE_CATEGORY_ICON_MAP: Record<string, string> = {
  // --- Transportation ---
  transportation: 'busStops',
  busTramFerry: 'busStops',
  bus: 'busStops',
  ferry: 'busStops',
  tram: 'tramStops',
  metro: 'metroStops',
  train: 'trainStops',
  personalMobility: 'busStops',
  sharedMobility: 'busStops',
  bikeParking: 'busStops',
  carMotorcycleParking: 'busStops',

  // --- Education ---
  education: 'schools',
  earlyChildhoodEducation: 'dayCares',
  daycareCenter: 'dayCares',
  higherEducation: 'schools',
  university: 'schools',
  otherEducation: 'schools',
  primaryAndSecondaryEducation: 'schools',
  highSchool: 'schools',

  // --- Eat / Drink ---
  eatDrink: 'restaurants',
  restaurant: 'restaurants',
  barNightClubs: 'restaurants',
  cafe: 'coffeePlaces',

  // --- Food retail ---
  foodRetail: 'supermarkets',
  supermarket: 'supermarkets',
  hypermarket: 'supermarkets',
  convenienceStore: 'supermarkets',
  kiosk: 'supermarkets',
  otherFoodRetail: 'supermarkets',

  // --- Shopping (retail) ---
  shopping: 'supermarkets',
  departmentStore: 'supermarkets',
  marketplace: 'supermarkets',
  shoppingCentre: 'supermarkets',
  specialityStores: 'supermarkets',
  personalCare: 'supermarkets',
  cosmeticSalon: 'supermarkets',
  hairdresser: 'supermarkets',

  // --- Other services (business / commerce) ---
  otherServices: 'supermarkets',
  professionalServicesBusinesses: 'supermarkets',

  // --- Health / care (proxy: daycare marker as a care-facility stand-in) ---
  health: 'dayCares',
  hospitalHealthStation: 'dayCares',
  otherHealthServices: 'dayCares',
  clinic: 'dayCares',
  dentist: 'dayCares',
  maternityAndChild: 'dayCares',
  optician: 'dayCares',
  pharmacyStore: 'dayCares',
  careService: 'dayCares',
  elderlyCare: 'dayCares',
  otherCaregivingServices: 'dayCares',

  // --- Public services (proxy: school marker as a public-institution stand-in) ---
  publicServices: 'schools',
  library: 'schools',
  park: 'schools',
  playground: 'schools',
  publicSpace: 'schools',
  religious: 'schools',

  // --- Culture / leisure & sport (proxy: cafe marker as a leisure stand-in) ---
  cultureLeisure: 'coffeePlaces',
  accommodation: 'coffeePlaces',
  culturalVenues: 'coffeePlaces',
  outdoorRecreation: 'coffeePlaces',
  tourismAttractionLandmark: 'coffeePlaces',
  sport: 'coffeePlaces',
  indoorSportsFacility: 'coffeePlaces',
  outdoorSportsFacility: 'coffeePlaces',
};

const SERVICE_ICON_NAMES = new Set(Object.values(SERVICE_CATEGORY_ICON_MAP));

export const getServiceMarkerIconId = (
  category?: string | null,
): string | undefined => {
  if (!category) return undefined;

  const iconName = SERVICE_ICON_NAMES.has(category)
    ? category
    : SERVICE_CATEGORY_ICON_MAP[category];

  return iconName ? `CHAOS_${iconName}_MARKER_ICON` : undefined;
};

export const loadServiceMarkersToMap = async (map: Map) => {
  if (!map) return;

  const servicesCategories = Object.values(SERVICES_MAIN_CATEGORIES).flat();

  await Promise.all(
    servicesCategories.map((category) => {
      addReamsCustomIconToMap({
        map,
        iconId: `CHAOS_${category.name}_MARKER_ICON`,
        iconSrc: category.iconMarkerSrc,
      });
      return null;
    }),
  );
};

export type MarketIndicator = 'above' | 'below' | 'at' | 'neutral';

export const MARKET_INDICATOR_COLORS: Record<MarketIndicator, string> = {
  above: '#5c3385',
  below: '#337785',
  at: '#855433',
  neutral: '#424242',
};

export const REAMS_MARKET_PIN_ICON_ID = (indicator: MarketIndicator) =>
  `RENT_NAVIGATOR_PROPERTY_PIN_${indicator.toUpperCase()}`;

/**
 * Creates an SVG pin image for use as a Mapbox map icon.
 * Renders at size × pixelRatio so the icon is crisp on HiDPI/retina displays.
 * Returns an HTMLImageElement and the pixelRatio used, for passing to map.addImage().
 */
const createMapPinSvg = (
  color: string,
  size = 40,
  pixelRatio = Math.min(window.devicePixelRatio ?? 2, 2),
): Promise<{ img: HTMLImageElement; pixelRatio: number }> => {
  const physicalSize = size * pixelRatio;

  const svg = `
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_775_2663)">
      <g filter="url(#filter0_d_775_2663)">
      <path d="M36.9723 75.0756L36.0271 74.4374C31.2964 71.1368 26.9005 67.3831 22.9022 63.2298C15.8769 55.8907 8 44.8893 8 31.9046C8 15.9302 21.0018 0 40 0C58.9982 0 72 15.9302 72 31.9095C72 44.8942 64.1231 55.8956 57.0978 63.2249C53.0995 67.3782 48.7036 71.1319 43.9729 74.4325C43.5725 74.7107 43.2574 74.9234 43.0277 75.0707C42.7328 75.2662 42.4362 75.4588 42.1387 75.6495C40.8381 76.4831 39.1626 76.4821 37.8605 75.6507C37.5633 75.4609 37.2669 75.2695 36.9723 75.0756Z" fill="white"/>
      <path d="M40 0.5C58.6935 0.5 71.4998 16.1769 71.5 31.9092C71.5 44.6956 63.7363 55.5758 56.7373 62.8779C52.7618 67.0075 48.3911 70.7397 43.6875 74.0215C43.289 74.2984 42.9798 74.5071 42.7578 74.6494L42.751 74.6543C42.459 74.8479 42.1649 75.039 41.8691 75.2285C40.7333 75.9565 39.2681 75.9561 38.1299 75.2295C37.8356 75.0416 37.5426 74.8524 37.252 74.6611L36.3125 74.0273H36.3135C31.9035 70.9505 27.7861 67.4773 24.0127 63.6523L23.2627 62.8828C16.2636 55.5706 8.5 44.6905 8.5 31.9043C8.50014 16.177 21.3064 0.5 40 0.5Z" stroke="#E8E9E4"/>
      </g>
      <circle cx="40" cy="32" r="22" fill="${color}"/>
      <path d="M37.6668 41.9167V34.9167H42.3335V41.9167H48.1668V32.5833H51.6668L40.0002 22.0833L28.3335 32.5833H31.8335V41.9167H37.6668Z" fill="white"/>
      </g>
      <defs>
      <filter id="filter0_d_775_2663" x="4" y="0" width="72" height="84.2744" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="4"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_775_2663"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_775_2663" result="shape"/>
      </filter>
      <clipPath id="clip0_775_2663">
      <rect width="80" height="80" fill="white"/>
      </clipPath>
      </defs>
  </svg>`.trim();

  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const img = new Image(physicalSize, physicalSize);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ img, pixelRatio });
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
};

export const registerMarketIndicatorPins = async (map: Map): Promise<void> => {
  if (!map) return;

  const indicators = Object.keys(MARKET_INDICATOR_COLORS) as MarketIndicator[];

  await Promise.all(
    indicators.map(async (indicator) => {
      const imageId = REAMS_MARKET_PIN_ICON_ID(indicator);
      if (map.hasImage(imageId)) return;

      const { img, pixelRatio } = await createMapPinSvg(
        MARKET_INDICATOR_COLORS[indicator],
      );
      if (!map.hasImage(imageId)) {
        map.addImage(imageId, img, { sdf: false, pixelRatio });
      }
    }),
  );
};

export const compareValues = (
  a: number | null | undefined,
  b: number | null | undefined,
  order: 'asc' | 'desc',
): number => {
  const aMissing = a == null || Number.isNaN(a);
  const bMissing = b == null || Number.isNaN(b);

  // If both missing, treat them as equal
  if (aMissing && bMissing) return 0;
  // If only A missing → it should go last
  if (aMissing) return 1;
  // If only B missing → it should go last
  if (bMissing) return -1;

  return order === 'asc' ? a - b : b - a;
};

export const compareNames = (
  a: string | null | undefined,
  b: string | null | undefined,
  order: 'asc' | 'desc',
): number => {
  const aMissing = !a || a.trim().length === 0;
  const bMissing = !b || b.trim().length === 0;

  // Both missing → treat as equal
  if (aMissing && bMissing) return 0;
  // Only A missing → it should go last
  if (aMissing) return 1;
  // Only B missing → it should go last
  if (bMissing) return -1;

  // Normalize case and locale for consistent comparison
  const aLower = a.toLocaleLowerCase();
  const bLower = b.toLocaleLowerCase();

  const comparison = aLower.localeCompare(bLower, undefined, {
    sensitivity: 'base',
  });

  return order === 'asc' ? comparison : -comparison;
};

export const getPropertyDetailsForm = (property: IProperty) => {
  if (!property) return null;
  return {
    id: property.id,
    name: property.name || property.address,
    address: property.address || property.name,
    contractRent: property?.rentPerMonth,
    size: property?.numberOfSquareMeters,
    numberOfRooms: property?.numberOfRooms
      ? parseInt(property?.numberOfRooms, 10)
      : undefined,
    floor: property?.floor,
    yearBuilt: property?.buildYear,
    propertyType: property?.dwellingType,
    amenities: property?.amenities || [],
    locationAnalysisType: property.locationAnalysisType as
      | 'radius'
      | 'postalCode'
      | undefined,
    radius: property.locationRadius,
    lng: property.geometry?.geometry?.coordinates?.[0],
    lat: property.geometry?.geometry?.coordinates?.[1],
  };
};

export const formatLongDate = (dateInput: string) => {
  if (!dateInput) return NO_VALUE_PLACEHOLDER;

  return new Date(dateInput).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const getMarketTrendTypeFunc = (
  rentDiffPercent: number | undefined | null,
) => {
  if (isNil(rentDiffPercent)) {
    return null;
  }
  if (Math.round(rentDiffPercent) > 0) {
    return MARKET_COMPARISON_TYPE.aboveMarket;
  }
  if (Math.round(rentDiffPercent) < 0) {
    return MARKET_COMPARISON_TYPE.belowMarket;
  }
  return MARKET_COMPARISON_TYPE.atMarket;
};
